import { todayIso, type Debt, type Transaction } from '@fin/shared';
import { useState } from 'react';
import { TxRow } from '../transactions/public.ts';
import { Button, ConfirmButton, Empty, ErrorBox, Field, Icon, Input, Loading, Modal, Money, NumberInput, PageHead, Progress, Segmented, Select, useAction } from '../../components/ui.tsx';
import { del, patch, post } from '../../lib/api.ts';
import { useContacts, useCurrencies, useDebts, useRates, useWallets } from '../../lib/data.ts';
import { dateLong, money, pct } from '../../lib/format.ts';
import { invalidate, invalidateMoney, useQuery } from '../../lib/query.ts';
import { setQuery, useLocation } from '../../lib/router.tsx';

type Dir = 'borrowed' | 'lent';

export function DebtsPage() {
  const { query } = useLocation();
  const q = useDebts();
  const rates = useRates();
  const dir = (query.get('dir') as Dir) || 'borrowed';
  const openId = Number(query.get('id')) || null;
  const [edit, setEdit] = useState<Debt | 'new' | null>(null);
  if (q.error && !q.data) return <ErrorBox error={q.error} onRetry={q.reload} />;
  if (!q.data) return <Loading />;

  const list = q.data.filter((d) => d.direction === dir);
  const open = list.filter((d) => !d.closedAt);
  const closed = list.filter((d) => d.closedAt);
  const toBase = (v: number, c: string) => rates.toBase(v, c) ?? 0;
  const remaining = open.reduce((s, d) => s + toBase(d.remaining, d.currency), 0);
  const monthly = open.reduce((s, d) => s + toBase(d.monthlyPayment ?? 0, d.currency), 0);

  return (
    <div className="page">
      <PageHead title="Долги" actions={<Button variant="primary" onClick={() => setEdit('new')}><Icon name="plus" /> Долг</Button>} />
      <Segmented label="Направление" value={dir} onChange={(v) => setQuery({ dir: v })} options={[
        { value: 'borrowed', label: 'Я должен' }, { value: 'lent', label: 'Мне должны' },
      ]} />

      <div className="stat-row">
        <div className="stat"><span className="muted small">Осталось {dir === 'borrowed' ? 'выплатить' : 'получить'}</span><strong><Money value={remaining} /></strong></div>
        {dir === 'borrowed' ? <div className="stat"><span className="muted small">Долговая нагрузка в месяц</span><strong><Money value={monthly} /></strong></div> : null}
        <div className="stat"><span className="muted small">Активных</span><strong>{open.length}</strong></div>
      </div>

      {open.length ? (
        <div className="debt-table" role="table" aria-label="Активные долги">
          <div className="debt-tr debt-th" role="row">
            <span role="columnheader">Долг</span><span role="columnheader">Оплачено</span><span role="columnheader">Сумма</span>
            <span role="columnheader">Прогресс</span><span role="columnheader">В месяц</span>
          </div>
          {open.map((d) => <DebtRow key={d.id} d={d} />)}
        </div>
      ) : <Empty>{dir === 'borrowed' ? 'Вы никому не должны.' : 'Вам никто не должен.'}</Empty>}

      {closed.length ? (
        <details className="archived">
          <summary>Закрытые ({closed.length})</summary>
          <div className="debt-table">{closed.map((d) => <DebtRow key={d.id} d={d} />)}</div>
        </details>
      ) : null}

      {openId ? <DebtDetail id={openId} onClose={() => setQuery({ id: null })} onEdit={(d) => setEdit(d)} /> : null}
      {edit ? <DebtModal debt={edit === 'new' ? undefined : edit} direction={dir} onClose={() => setEdit(null)} /> : null}
    </div>
  );
}

function DebtRow({ d }: { d: Debt }) {
  return (
    <button type="button" className={`debt-tr debt-${d.direction}`} role="row" onClick={() => setQuery({ id: d.id })}>
      <span className="debt-title" role="cell">{d.title}<span className="muted small block">{d.contactName ?? '—'}{d.closedAt ? `, закрыт ${dateLong(d.closedAt)}` : ''}</span></span>
      <span role="cell" className="num"><Money value={d.paid} currency={d.currency} /></span>
      <span role="cell" className="num"><Money value={d.total} currency={d.currency} /></span>
      <span role="cell" className="debt-progress"><Progress value={d.progress} tone={d.direction === 'lent' ? 'amber' : 'debt'} label={d.title} /><span className="small">{pct(d.progress)}</span></span>
      <span role="cell" className="num">{d.monthlyPayment ? money(d.monthlyPayment, d.currency) : '—'}</span>
    </button>
  );
}

function DebtDetail({ id, onClose, onEdit }: { id: number; onClose: () => void; onEdit: (d: Debt) => void }) {
  const q = useQuery<Debt & { transactions: Transaction[] }>(`/debts/${id}`);
  const wallets = useWallets();
  const [walletId, setWalletId] = useState<number | ''>('');
  const [amount, setAmount] = useState<number | null>(null);
  const [date, setDate] = useState(todayIso());
  const { busy, run } = useAction();
  const d = q.data;
  const defaultWallet = (wallets.data ?? []).find((w) => !w.archived && w.currency === d?.currency && w.type === 'bank');
  // the monthly payment is offered, but never more than is still owed (Money Lover's «Нужно»)
  const suggested = d ? (d.monthlyPayment ? Math.min(d.monthlyPayment, d.remaining) : null) : null;
  const payAmount = amount ?? suggested;
  const overpay = !!d && payAmount !== null && payAmount > d.remaining + 0.005;

  return (
    <Modal wide title={d?.title ?? 'Долг'} onClose={onClose} footer={d ? <>
      <ConfirmButton size="md" confirmText="Удалить долг? Операции останутся" onConfirm={async () => { await del(`/debts/${d.id}`); invalidateMoney(); onClose(); }}>Удалить</ConfirmButton>
      <Button onClick={() => { onEdit(d); }}><Icon name="edit" size={16} /> Изменить</Button>
    </> : undefined}>
      {!d ? <Loading /> : (
        <div className="debt-detail">
          <div className="stat-row">
            <div className="stat"><span className="muted small">Оплачено</span><strong><Money value={d.paid} currency={d.currency} /></strong></div>
            <div className="stat"><span className="muted small">Сумма{d.totalPayable && d.totalPayable !== d.principal ? ' с процентами' : ''}</span><strong><Money value={d.total} currency={d.currency} /></strong></div>
            <div className="stat"><span className="muted small">Осталось</span><strong><Money value={d.remaining} currency={d.currency} /></strong></div>
          </div>
          <Progress value={d.progress} tone={d.direction === 'lent' ? 'amber' : 'debt'} label="Прогресс" />
          <p className="muted small">
            {d.contactName ? `${d.contactName}. ` : ''}С {dateLong(d.startDate, true)}
            {d.monthlyPayment ? <>, платёж <span className="money">{money(d.monthlyPayment, d.currency)}</span> в месяц</> : ''}
            {d.totalPayable && d.totalPayable !== d.principal ? <>, получено <span className="money">{money(d.principal, d.currency)}</span></> : ''}.
            {d.note ? ` ${d.note}` : ''}
          </p>

          {!d.closedAt ? (
            <form className="pay-form" onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                if (!payAmount) throw new Error('Укажите сумму');
                if (overpay) throw new Error(`Сумма больше остатка: ${money(d.remaining, d.currency)}`);
                await post(`/debts/${d.id}/payments`, { walletId: walletId || defaultWallet?.id, amount: payAmount, date });
                setAmount(null);
                invalidateMoney();
                invalidate(`/debts/${d.id}`);
              }, 'Платёж записан');
            }}>
              <div className="pay-head">
                <h3>{d.direction === 'borrowed' ? 'Внести платёж' : 'Получить возврат'}</h3>
                <span className="muted small">Нужно <Money value={d.remaining} currency={d.currency} /></span>
              </div>
              <div className="form-row">
                <Field label="Сумма" className="grow" hint={overpay ? <span className="neg">Больше остатка долга</span> : undefined}>
                  <NumberInput value={payAmount} onChange={setAmount} />
                </Field>
                <Field label="Кошелёк" className="grow">
                  <Select value={walletId || defaultWallet?.id || ''} onChange={(e) => setWalletId(Number(e.target.value))}>
                    {(wallets.data ?? []).filter((w) => !w.archived && w.currency === d.currency).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </Select>
                </Field>
                <Field label="Дата"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
              </div>
              <div className="row-gap">
                <Button type="submit" variant="primary" busy={busy} disabled={overpay || !payAmount}>Записать</Button>
                {payAmount !== d.remaining ? <Button type="button" variant="ghost" onClick={() => setAmount(d.remaining)}>Весь остаток</Button> : null}
              </div>
            </form>
          ) : <p className="tag-ok">Долг закрыт {dateLong(d.closedAt, true)}</p>}

          <h3>История</h3>
          <ul className="tx-list">{d.transactions.map((t) => <TxRow key={t.id} t={t} showDate />)}</ul>
          {!d.transactions.length ? <p className="muted">Операций нет.</p> : null}
        </div>
      )}
    </Modal>
  );
}

function DebtModal({ debt, direction, onClose }: { debt?: Debt; direction: Dir; onClose: () => void }) {
  const contacts = useContacts();
  const currencies = useCurrencies();
  const [f, setF] = useState({
    direction: debt?.direction ?? direction,
    title: debt?.title ?? '',
    contactId: debt?.contactId ?? null as number | null,
    contactName: '',
    currency: debt?.currency ?? 'RUB',
    principal: debt?.principal ?? null as number | null,
    totalPayable: debt?.totalPayable ?? null as number | null,
    paidOffset: debt?.paidOffset ?? 0 as number | null,
    monthlyPayment: debt?.monthlyPayment ?? null as number | null,
    startDate: debt?.startDate ?? todayIso(),
    dueDate: debt?.dueDate ?? '',
    note: debt?.note ?? '',
    url: debt?.url ?? '',
    closed: !!debt?.closedAt,
  });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const { busy, run } = useAction();
  const save = () => run(async () => {
    const body = {
      direction: f.direction, title: f.title, contactId: f.contactId, contactName: f.contactName || undefined,
      currency: f.currency, principal: f.principal ?? 0, totalPayable: f.totalPayable, paidOffset: f.paidOffset ?? 0,
      monthlyPayment: f.monthlyPayment, startDate: f.startDate, dueDate: f.dueDate || null, note: f.note, url: f.url,
      closedAt: f.closed ? debt?.closedAt ?? todayIso() : null,
    };
    if (debt) await patch(`/debts/${debt.id}`, body); else await post('/debts', body);
    invalidateMoney();
    if (debt) invalidate(`/debts/${debt.id}`);
    onClose();
  }, 'Долг сохранён');

  return (
    <Modal className="modal-form" title={debt ? 'Изменить долг' : 'Новый долг'} onClose={onClose} footer={<>
      <span /><div className="row-gap"><Button onClick={onClose}>Отмена</Button><Button variant="primary" busy={busy} onClick={save}>Сохранить</Button></div>
    </>}>
      <form className="form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        {!debt ? <Segmented label="Направление" value={f.direction} onChange={(v) => set({ direction: v })} options={[{ value: 'borrowed', label: 'Я должен' }, { value: 'lent', label: 'Мне должны' }]} /> : null}
        <Field label="Название"><Input value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder="Например, Сплит на ПК" required autoFocus /></Field>
        <div className="form-row">
          <Field label="Кредитор / должник" className="grow">
            <Select value={f.contactId ?? ''} onChange={(e) => set({ contactId: e.target.value ? Number(e.target.value) : null })}>
              <option value="">—</option>
              {(contacts.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          {!f.contactId ? <Field label="Или новый" className="grow"><Input value={f.contactName} onChange={(e) => set({ contactName: e.target.value })} /></Field> : null}
        </div>
        <div className="form-row">
          <Field label="Получено / выдано" className="grow"><NumberInput value={f.principal} onChange={(v) => set({ principal: v })} /></Field>
          <Field label="Валюта">
            <Select value={f.currency} onChange={(e) => set({ currency: e.target.value })}>
              {(currencies.data ?? [{ code: f.currency }]).map((c) => <option key={c.code}>{c.code}</option>)}
            </Select>
          </Field>
        </div>
        <div className="form-row">
          <Field label="Всего к оплате" className="grow" hint="С процентами и комиссиями, если есть"><NumberInput value={f.totalPayable} onChange={(v) => set({ totalPayable: v })} /></Field>
          <Field label="Платёж в месяц" className="grow"><NumberInput value={f.monthlyPayment} onChange={(v) => set({ monthlyPayment: v })} /></Field>
        </div>
        <div className="form-row">
          <Field label="Дата начала" className="grow"><Input type="date" value={f.startDate} onChange={(e) => set({ startDate: e.target.value })} /></Field>
          <Field label="Погасить до" className="grow"><Input type="date" value={f.dueDate} onChange={(e) => set({ dueDate: e.target.value })} /></Field>
        </div>
        <Field label="Оплачено вне трекера" hint="Платежи до начала учёта"><NumberInput value={f.paidOffset} onChange={(v) => set({ paidOffset: v })} /></Field>
        <Field label="Ссылка"><Input value={f.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://" /></Field>
        <Field label="Заметка"><Input value={f.note} onChange={(e) => set({ note: e.target.value })} /></Field>
        {debt ? <label className="check"><input type="checkbox" checked={f.closed} onChange={(e) => set({ closed: e.target.checked })} /> Долг закрыт</label> : null}
        <p className="muted small">Сумма на балансе не меняется. Чтобы деньги поступили или ушли из кошелька, добавьте операцию «Долг» в разделе «Операции».</p>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
